import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "react-select";
import { selectAllCompanyUsersById } from "app/containers/Global/slice";
import Button from "app/storybookComponents/Button";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import { useAppSelector } from "utils/redux/hooks";
import Paginator from "../SortableTable/Paginator";
import SimplePopover from "./SimplePopover";
import Loading from "app/storybookComponents/Loading";
import { useDebounceCallback } from "utils/hooks";
import { Link } from "react-router-dom";
import { getSelectProps } from "utils/helperFunctions";

interface Props {
  title: string;
  description: string;
  userAccountIds: number[];
  additionalInfo?: string;
  onRemove?: () => void;
  userTagsById?: Record<number, string>;
  emptyState?: JSX.Element;
}

const ROWS_PER_PAGE = 5;

const CardWithPopoverUsers = ({
  title,
  description,
  userAccountIds,
  onRemove,
  additionalInfo,
  userTagsById,
  emptyState,
}: Readonly<Props>) => {
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const [searchInput, setSearchInput] = useState("");
  const [displayInput, setDisplayInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [displayInput]);

  const onSearchChange = useDebounceCallback((value: string) => {
    setSearchInput(value);
  }, 500);

  const getUserRow = useCallback(
    (userAccountId: number) => {
      const user = usersById[userAccountId];
      const name =
        `${user.firstName} ${user.lastName}`.trim() || user.emailAddress;
      const jobTitle = user.jobTitle ?? "No Job Title";
      const userTag = userTagsById?.[userAccountId];
      return (
        <tr key={userAccountId} className="simple-user-table__row">
          <td className="simple-user-table__cell">
            <div>
              <Link to={`/UserGuide/${userAccountId}`}>{name}</Link>
              <p className="small-grey-text">{jobTitle}</p>
            </div>
            {userTag && <div className="label-tag grey no-wrap">{userTag}</div>}
          </td>
        </tr>
      );
    },
    [usersById, userTagsById]
  );

  const allFilteredUsers = useMemo(() => {
    if (!searchInput) return userAccountIds;
    const filteredUsers = userAccountIds.filter((userAccountId) => {
      const user = usersById[userAccountId];
      const name =
        `${user.firstName} ${user.lastName} ${user.emailAddress}`.trim();
      return name.toLowerCase().includes(searchInput.toLowerCase());
    });
    return filteredUsers;
  }, [userAccountIds, searchInput, usersById]);

  const userRowsShowing = useMemo(() => {
    const splicedUsers = allFilteredUsers
      .filter((userAccountId) => !!usersById[userAccountId])
      .slice((currentPage - 1) * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE);

    return splicedUsers.map((userAccountId) => getUserRow(userAccountId));
  }, [allFilteredUsers, currentPage, getUserRow, usersById]);

  const getPopoverSearchInput = () => {
    const { selectStyles, components } = getSelectProps("search");
    return (
      <Select
        placeholder={"Search by name or email..."}
        isClearable={true}
        isSearchable={true}
        components={components}
        inputValue={displayInput}
        styles={selectStyles}
        menuIsOpen={false}
        onInputChange={(e, actionMeta) => {
          if (actionMeta.action === "input-change") {
            onSearchChange(e);
            setDisplayInput(e);
          }
        }}
      />
    );
  };

  const getAdditionalInfo = () => {
    if (!additionalInfo) return null;
    return ` | ${additionalInfo}`;
  };

  const getPopoverContent = () => {
    return (
      <div className="column-gap-16px">
        <div>
          <p className="fw-bold">{title}</p>
          <p className="small-grey-text">
            {description}
            {getAdditionalInfo()}
          </p>
        </div>
        {getPopoverSearchInput()}
        {getPopoverTable()}
      </div>
    );
  };

  const getEmptyState = () => {
    if (!displayInput) {
      return (
        emptyState ?? (
          <div className="line-chart-empty-state">
            <div>
              <div className="line-chart-empty-state__title">
                <p>No users found</p>
              </div>
              <div className="line-chart-empty-state__description">
                <p>Currently, there are no members in entity.</p>
              </div>
            </div>
          </div>
        )
      );
    }

    return (
      <div className="line-chart-empty-state">
        <div>
          <div className="line-chart-empty-state__title">
            <p>No search results found</p>
          </div>
          <div className="line-chart-empty-state__description">
            <p>
              No results matching "{displayInput}" found. Please try a different
              name.
            </p>
          </div>
        </div>
      </div>
    );
  };

  const getPopoverTable = () => {
    if (searchInput !== displayInput) return <Loading />;
    if (!allFilteredUsers.length) return getEmptyState();

    return (
      <>
        <table className="simple-user-table">
          <tbody>{userRowsShowing}</tbody>
        </table>
        <div className="mt-3 d-flex">
          <Paginator
            pageRangeDisplayed={5}
            pageCount={Math.ceil(allFilteredUsers.length / ROWS_PER_PAGE)}
            onPageChange={(selectedItem) => {
              setCurrentPage(selectedItem.selected + 1);
            }}
            forcePage={currentPage - 1}
          />
        </div>
      </>
    );
  };

  return (
    <Card className="small-grey-selected-option">
      <div>
        <p className="small-grey-selected-option__title">{title}</p>
        <SimplePopover
          hideTitleAndCloseButton
          popoverContent={getPopoverContent()}
          trigger="click"
          popoverClassName="popover-width-400px"
        >
          <p className="small-grey-selected-option__description">
            {description}
          </p>
        </SimplePopover>
      </div>
      <div>
        <Button
          variant="secondary-gray"
          className="border-0"
          onClick={onRemove}
          style={{ marginRight: "-8px" }}
        >
          <FontAwesomeIcon icon="xmark" />
        </Button>
      </div>
    </Card>
  );
};

export default CardWithPopoverUsers;
